import dynamic from 'next/dynamic'
import { type FC } from 'react'

import { useDeviceDetectorContext } from '../../../device-detector'
import { ErrorBoundary } from '../../../error-boundary'
import { useGlobalConfigContext } from '../../../global-config'
import { MediServiceFurtherInformation } from '../../../medi-service'
import { TENANT } from '../../../tenant'
import { PdpRightAdBanner } from '../../ads/components/PdpRightAdBanner/PdpRightAdBanner'
import { productAccessorIsPrescriptionProduct } from '../../model/productAccessor/productAccessorIsPrescriptionProduct'
import { DynamicProductCrossSellBuyTogether } from '../ProductCrossSell/ProductCrossSellBuyTogether'
import { ProductDescription } from '../ProductDescription'
import { ProductFaqAndReviews } from '../ProductFaqAndReviews'
import { ProductKeyFacts } from '../ProductKeyFacts'
import { ProductOverview } from '../ProductOverview'
import { useProductPageContext } from '../productPageContext'
import { ProductPrescriptionNotificationRequired } from '../ProductPrescriptionNotificationRequired'
import { ProductPrescriptionNotificationVideo } from '../ProductPrescriptionNotificationVideo'

const ProductAvailableCrossSellSliderWidget = dynamic(async () =>
  import('../ProductAvailableCrossSellSliderWidget').then(
    (mod) => mod.ProductAvailableCrossSellSliderWidgetExperiment,
  ),
)

/* eslint-disable react/no-multi-comp -- this has been done to allow for any logic to be added in the individual FCs. */
const BannerAndCrosssellBuyTogether: FC = () => {
  return (
    <>
      <PdpRightAdBanner />
      <ErrorBoundary>
        <DynamicProductCrossSellBuyTogether />
      </ErrorBoundary>
    </>
  )
}

const ProductAvailableCsw: FC = () => {
  return (
    <ErrorBoundary>
      <ProductAvailableCrossSellSliderWidget />
    </ErrorBoundary>
  )
}
const DefaultCswPosition: FC = () => {
  const { tenant } = useGlobalConfigContext()
  const { product } = useProductPageContext()

  /**
   * We show RX products in CH in order to increase the sales of prescription medicine in Switzerland through the use of Mediservice
   * We need to restrict several sections of the PDP and keep the page clear and informative regarding the product
   */
  const isPrescriptionProductInChTenant =
    productAccessorIsPrescriptionProduct(product) && TENANT.CH === tenant

  return (
    <>
      <ProductOverview />
      <ProductKeyFacts />
      <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
        <ProductDescription className="flex-1" />
        {!isPrescriptionProductInChTenant && (
          <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
        )}
        <div className="flex w-full flex-1 flex-col gap-5">
          {!isPrescriptionProductInChTenant && (
            <>
              <ProductPrescriptionNotificationRequired />
              <ProductAvailableCsw />
              <ProductPrescriptionNotificationVideo />
              <BannerAndCrosssellBuyTogether />
            </>
          )}

          {isPrescriptionProductInChTenant ? (
            <MediServiceFurtherInformation
              className="pb-6 pt-0"
              displayInColumn
            />
          ) : null}
        </div>
      </div>
    </>
  )
}
const PdpLowerProductDetailsWithoutProductAvailableCsw: FC = () => {
  const { tenant } = useGlobalConfigContext()
  const { product } = useProductPageContext()

  const isPrescriptionProductInChTenant =
    productAccessorIsPrescriptionProduct(product) && TENANT.CH === tenant

  return (
    <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
      <ProductDescription className="flex-1" />
      <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
      <div className="flex w-full flex-1 flex-col gap-5">
        {!isPrescriptionProductInChTenant && (
          <>
            <ProductPrescriptionNotificationRequired />
            <ProductPrescriptionNotificationVideo />
            <BannerAndCrosssellBuyTogether />
          </>
        )}
      </div>
    </div>
  )
}

export const ProductPageOverview: FC = () => {
  const { isMobile } = useDeviceDetectorContext()
  const { tenant } = useGlobalConfigContext()
  if (!isMobile) {
    return <DefaultCswPosition />
  }
  if (tenant === TENANT.BE) {
    return (
      <>
        <ProductOverview />
        <ProductKeyFacts />
        <div className="mb-5 grid grid-cols-1 gap-5 tablet:grid-cols-2">
          <ProductDescription className="flex-1" />
          <ProductAvailableCsw />
          <ProductFaqAndReviews className="tablet:order-1 tablet:col-span-2" />
          <div className="flex w-full flex-1 flex-col gap-5">
            <ProductPrescriptionNotificationRequired />
            <ProductPrescriptionNotificationVideo />
            <BannerAndCrosssellBuyTogether />
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <ProductOverview ProductAvailableCsw={ProductAvailableCsw} />
      <ProductKeyFacts />
      <PdpLowerProductDetailsWithoutProductAvailableCsw />
    </>
  )
}
/* eslint-enable react/no-multi-comp */
